import { useMemo } from 'react';

import config from 'config';
import { useRouter } from 'next/router';

import featureNames from '~/shared/constants/featureFlags';
import { buildMasterProductGroupCodeObjects } from '~/shared/utils/buildMasterProductGroupCodeObjects';
import useHasFeature from '~/shared/utils/useHasFeature';

import { mergeUnifiedPrimaryProductsAndRelatedData } from '../transformers/mergeUnifiedPrimaryProductsAndRelatedData';
import { NormalizedCioResponseInfiniteData } from '../transformers/normalizeCioResponse';
import { CIOSearchProductGridResponse } from '../types/gridResponses';

import useBatchedGqlProductPricing from './useBatchedGqlProductPricing';
import useBatchedGqlRelatedProducts from './useBatchedGqlRelatedProducts';
import useCioBrowseFacets from './useCioBrowseFacets';
import useCioSearchProducts from './useCioSearchProducts';
import useFacetTracking from './useFacetTracking';

type CIOSearchProductsResponse = {
  data: NormalizedCioResponseInfiniteData<any>;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  selectedSortOption: string | null;
  searchQuery: string | null;
  customFacetFilters: Record<string, any>;
  setCustomFacetFilters: (filters: Record<string, any>) => void;
};

const facetBlacklist = config.get<string[]>(
  'public.brand.constructor.facetBlacklist'
);

const useCioSearchAndGqlProducts = (): CIOSearchProductGridResponse => {
  const { selectedUrlFilters, allRegisteredFacets, allRegisteredFacetNames } =
    useCioBrowseFacets();

  const { enabled: isProductsByGroupCodeQueryEnabled } = useHasFeature(
    featureNames.PRODUCTS_BY_GROUP_CODE_QUERY
  );

  const {
    data: cioSearchResults,
    isLoading: isPrimaryProductsLoading,
    isFetching: isPrimaryProductsFetching,
    // error,
    fetchNextPage,
    hasNextPage,
    selectedSortOption,
    searchQuery,
    customFacetFilters,
    setCustomFacetFilters,
  } = useCioSearchProducts({
    selectedUrlFilters,
    allRegisteredFacets,
  }) as CIOSearchProductsResponse;

  const masterProductIds = useMemo(() => {
    return cioSearchResults?.products?.map((result) => result?.masterProductId);
  }, [cioSearchResults]);

  // dedupe by groupCode, merging masterProductIds
  const masterProductGroupCodeObjects = useMemo(
    () => buildMasterProductGroupCodeObjects(cioSearchResults?.products ?? []),
    [cioSearchResults]
  );

  const {
    data: pricingData,
    isLoading: isProductPricingLoading,
    isFetching: isProductPricingFetching,
  } = useBatchedGqlProductPricing(masterProductIds);
  const {
    data: relatedProductsData,
    isLoading: isRelatedProductsLoading,
    isFetching: isRelatedProductsFetching,
  } = useBatchedGqlRelatedProducts({
    masterProductIds,
    masterProductGroupCodeObjects,
  });

  // Combine the primary products with the pricingData and relatedProductsData
  const mergedProducts = useMemo(
    () =>
      mergeUnifiedPrimaryProductsAndRelatedData({
        primaryProducts: cioSearchResults?.products,
        pricingData,
        relatedProductsData,
        isProductsByGroupCodeQueryEnabled,
      }),
    [
      cioSearchResults,
      pricingData,
      relatedProductsData,
      isProductsByGroupCodeQueryEnabled,
    ]
  );

  const totalNumResults = cioSearchResults?.pages?.[0]?.totalNumResults;

  const facets = useMemo(() => {
    const rawFacets = cioSearchResults?.pages?.[0]?.response?.facets;
    return rawFacets?.filter((facet) => !facetBlacklist.includes(facet.name));
  }, [cioSearchResults]);

  const sortOptions = useMemo(() => {
    return cioSearchResults?.pages?.[0]?.response?.sort_options;
  }, [cioSearchResults]);

  useFacetTracking({ selectedUrlFilters, selectedSortOption });

  // Handle Redirects
  const router = useRouter();
  const redirectUrl = cioSearchResults?.pages?.[0]?.redirectUrl;
  if (redirectUrl) {
    router.replace(redirectUrl);
  }

  return {
    redirectUrl,
    products: mergedProducts,
    fetchNextPage,
    hasNextPage,
    facets,
    sortOptions,
    selectedUrlFilters,
    allRegisteredFacets,
    allRegisteredFacetNames,
    totalNumResults,
    // Show loading state instead of no results when redirectUrl is present
    isPrimaryProductsLoading: isPrimaryProductsLoading || Boolean(redirectUrl),
    isPrimaryProductsFetching,
    isRelatedProductsLoading,
    isRelatedProductsFetching,
    isProductPricingLoading,
    isProductPricingFetching,
    searchQuery,
    customFacetFilters,
    setCustomFacetFilters,
  };
};

export default useCioSearchAndGqlProducts;
